import { initialActionState } from 'src/store/modules/runbooks/actions/ActionsHelper'
import ActionsHelper from '../actions/ActionsHelper'

/**
 * Transforms template actions into runbook actions.
 * Preserves hierarchy and sequence.
 *
 * @param {Array} templateActions List of template actions from backend
 * @returns {Object} { byId, uids, allUids, firstAction, ciToUid }
 */
export const transformTemplateActions = (templateActions) => {
    if (!templateActions || templateActions.length === 0) {
        return {
            byId: {},
            uids: [],
            allUids: [],
            firstAction: null,
            ciToUid: {},
            lastUid: 1,
            maxDepth: 0,
        }
    }

    const byId = {}
    const templateIdToUid = {}
    const allUids = []

    // 1. Assign sequential UIDs to each template action (starting from 1, like normal flow)
    templateActions.forEach((ta, index) => {
        const uid = index + 1 // Generate sequential UIDs starting from 1
        templateIdToUid[ta.id] = uid
        allUids.push(uid)
    })

    // 2. Create action objects
    templateActions.forEach((ta) => {
        const uid = templateIdToUid[ta.id]

        byId[uid] = {
            ...initialActionState,
            uid,
            id: null, // New actions don't have DB IDs yet
            name: ta.name,
            sequence_id: ta.sequence_id,
            treeParent: ta.parent ? templateIdToUid[ta.parent] : null,
            visible: !ta.parent, // Only top-level actions are visible initially
            expanded: false, // Ensure they start collapsed
            depth: ta.depth || 0,
            description: ta.description || '',
            device: ta.device || '',
            deviceClass: ta.device_class || '',
            environment: ta.environment || '',
            ownerUserId: ta.owner_user_id || null,
            ownerTeamId: ta.owner_team_id || null,
            ownerSupplierId: ta.owner_supplier_id || null,
            cmdbOwnerTeamId: ta.cmdb_owner_team_id || null,
            freezeOwnerTeam: ta.freeze_owner_team || false,
            reverse: ta.reverse !== undefined ? ta.reverse : true,
            autoSucceed: ta.auto_succeed !== undefined ? ta.auto_succeed : false,
            monitored: ta.monitored !== undefined ? ta.monitored : false,
            customUrl: ta.custom_url || '',
            color: ta.color || '',
            // Initialize arrays
            children: [],
            treeChildren: [],
            parents: [],
        }
    })

    // 3. Establish hierarchy, depth, and sibling relationships
    const topLevelActions = []

    // Sort all actions by sequence_id to ensure order
    const sortedActions = [...templateActions].sort((a, b) => a.sequence_id - b.sequence_id)

    // Calculate depths
    const getDepth = (uid) => {
        let depth = 0
        let current = byId[uid]
        while (current && current.treeParent) {
            depth++
            current = byId[current.treeParent]
        }
        return depth
    }

    allUids.forEach((uid) => {
        byId[uid].depth = getDepth(uid)
    })

    // Group actions by parent
    const childrenByParent = {}
    sortedActions.forEach(ta => {
        const parentId = ta.parent || 'root'
        if (!childrenByParent[parentId]) {
            childrenByParent[parentId] = []
        }
        childrenByParent[parentId].push(ta)
    })

    // Process each group to set treeChildren, children (starts), and siblings
    Object.keys(childrenByParent).forEach(parentId => {
        const children = childrenByParent[parentId]
        const parentUid = parentId === 'root' ? null : templateIdToUid[parentId]

        for (let i = 0; i < children.length; i++) {
            const currentTa = children[i]
            const currentUid = templateIdToUid[currentTa.id]

            if (parentId === 'root') {
                topLevelActions.push(currentUid)
            } else {
                byId[parentUid].treeChildren.push(currentUid)
                // If it's the first child in this group, it's the "start" child for logic
                if (i === 0) {
                    byId[parentUid].children.push(currentUid)
                }
            }

            // Siblings and logic execution chain
            if (i > 0) {
                const prevUid = templateIdToUid[children[i - 1].id]
                byId[currentUid].prevSibling = prevUid
                byId[prevUid].nextSibling = currentUid
            }
        }
    })

    // 4. Generate the full sorted uids list using ActionsHelper
    const firstAction = topLevelActions.length > 0 ? topLevelActions[0] : null
    const helper = new ActionsHelper({
        actions: {
            byId,
            topAction: firstAction,
            lastUid: allUids.length + 1, // Next available UID after sequential template actions
            ciToUid: {}
        }
    })

    const maxDepth = allUids.length > 0 ? Math.max(...allUids.map(uid => byId[uid].depth)) : 0

    return {
        byId,
        uids: helper.createActionsList(),
        allUids,
        firstAction,
        lastUid: helper.lastUid,
        maxDepth,
        ciToUid: {},
    }
}
